import React, {
  useMemo,
  useCallback,
}                              from 'react';
import styled                  from 'styled-components';
import {
  IAuction,
  TStatusOption,
  IFetchBid,
  IAuctionsResponse,
}                              from '@typings/auction';
import { ICreditUnion }        from '@typings/accountManagement';
import { TDataTableRow }       from '@typings/components';
import { ISearchQueryParams }  from '@typings/search';
import { EAppPage }            from '@enums/common';
import { EAuctionStatus }      from '@enums/auction';
import {
  EAccountPage,
  EAccountSubpage,
}                              from '@enums/accountManagement';
import { useToggle }           from '@utils/useToggle';
import {
  useFetchAuctionsStatuses,
  useFetchAuctions,
  useFetchAuctionBidData,
}                              from '@modules/auctions/queries/auctionsQueries';
import {
  capitalize,
  formatTitle,
  formatCurrency,
  formatAuctionDate,
  getRetailPercentage,
  getWholesalePercentage,
  isBeforeNow,
  getIsShowBidList,
  getIsShownDetails,
}                              from '@utils/commonFunctions';
import { AUCTION_STATUS_NAME } from '@modules/auctions/utils/suggestions';
import { AccountAuctionsPage } from '@modules/accountManagement/pages/AccountAuctionsPage';
import {
  LinkButton,
  VinWrapper,
}                              from '@components/index';
import { BidListModal }        from '@modules/auctions/components/BidListModal';

const TitleColumn = styled.span`
  font-size   : 15px;
  font-weight : 400;
`;

const VinColumn = styled.span`
  p {
    font-weight : 400;
    font-size   : 12px;
  }

  span {
    font-size: 14px;
  }
`;

const statusSectionsOrder = [
  EAuctionStatus.Active,
  EAuctionStatus.Ended,
  EAuctionStatus.Pending,
  EAuctionStatus.PendingNoPhotos,
  EAuctionStatus.Canceled,
  EAuctionStatus.SavedDraft,
  EAuctionStatus.SavedDraftNoPhotos,
  EAuctionStatus.Archived,
];

const getSectionRow = (
  section          : EAuctionStatus,
  auctionsStatuses : TStatusOption[],
): TDataTableRow => {
  const itemsCount = (auctionsStatuses ?? []).find(
    (filter: TStatusOption): boolean => filter.value === section,
  )?.count ?? 0;

  return [{
    value   : `${section}`,
    title   : `${AUCTION_STATUS_NAME[section]}${itemsCount ? ` (${itemsCount})` : ''}`,
    section : true,
  }];
};

const getItemRows = (
  section            : EAuctionStatus,
  auctions           : IAuction[],
  push               : (path: string) => void,
  handleBidListModal : (auction: IAuction) => void,
): TDataTableRow[] =>
  (auctions ?? [])
    .filter(
      (auction: IAuction): boolean =>
        auction.status === section,
    )
    .reduce(
      (itemRows: TDataTableRow[], auction: IAuction): TDataTableRow[] => {
        const wholesalePercentage = getWholesalePercentage(auction);
        const retailPercentage    = getRetailPercentage(auction);
        const isShowBidList       = getIsShowBidList(auction.status);
        const isShownDetailsForm  = getIsShownDetails(auction.status);

        const row: TDataTableRow = [
          {
            value : `${auction.status}`,
            meta  : true,
          },
          {
            value: <TitleColumn>{formatTitle(auction)}</TitleColumn>,
          },
          {
            value: (
              <VinColumn>
                <p>
                  {isBeforeNow(auction.endDate) ? 'Ended ' : 'Ends '}
                  {formatAuctionDate(auction.endDate, 'MM/DD/YY hh:mm A z')}
                </p>
                <VinWrapper>{auction.vehicle?.vin ?? ''}</VinWrapper>
              </VinColumn>
            ),
          },
          {
            align : 'center',
            value : (
              <>
                {isShowBidList && (
                  <LinkButton
                    label = "Bid List"
                    onClick = {() => handleBidListModal && handleBidListModal(auction)}
                  />
                )}

                {isShownDetailsForm && (
                  <LinkButton
                    label   = {capitalize(EAccountSubpage.Details)}
                    onClick = {
                      (): void =>
                        push(`${EAppPage.AccountManagement}/${auction.cuid}/${EAccountPage.Auctions}/${auction.id}/${EAccountSubpage.Details}`)
                    }
                  />
                )}

                <LinkButton
                  label   = {capitalize(EAccountSubpage.Photos)}
                  onClick = {
                    (): void =>
                      push(`${EAppPage.AccountManagement}/${auction.cuid}/${EAccountPage.Auctions}/${auction.id}/${EAccountSubpage.Photos}`)
                  }
                />
              </>
            ),
          },
          {
            value : auction.watches || '—',
            align : 'center',
          },
          {
            value : auction.views || '—',
            align : 'center',
          },
          {
            value : auction.bidsCount || '—',
            align : 'center',
          },
          {
            value : formatCurrency(auction.currentHighestBid),
            align : 'center',
          },
          {
            value : wholesalePercentage ? `${wholesalePercentage}%` : '—',
            align : 'center',
          },
          {
            value : retailPercentage ? `${retailPercentage}%` : '—',
            align : 'center',
          },
        ];

        itemRows.push(row);

        return itemRows;
      }, [],
    );

const getTableRows = (
  auctions           : IAuction[],
  auctionsStatuses   : TStatusOption[],
  push               : (path: string) => void,
  handleBidListModal : (auction: IAuction) => void,
): TDataTableRow[] =>
  statusSectionsOrder.reduce(
    (tableRows: TDataTableRow[], section: EAuctionStatus): TDataTableRow[] => {
      const sectionRow = getSectionRow(section, auctionsStatuses);

      tableRows.push(sectionRow);

      const itemRows = getItemRows(section, auctions, push, handleBidListModal);

      tableRows.push(...itemRows);

      return tableRows;
    }, [],
  );

interface IProps {
  push        : (path: string) => void;
  accountData : ICreditUnion;
  cuid        : string;
}

export const AccountAuctionsContainer: React.FC<IProps> = ({
  push,
  accountData,
  cuid,
}) => {
  const [isBidListModal, toggleIsBidListModal] = useToggle();

  const params = useMemo<ISearchQueryParams>(
    () => ({
      skip    : 0,
      size    : 1000,
      filters : `(Status ne -1 and Status ne 8 and Status ne 9 and CUID eq '${cuid}')`,
      orderBy : 'TimeStamp desc',
    }),
    [cuid],
  );

  // #region API INTEGRATION
  const {
    mutate    : fetchBidData,
    data      : currentBid = [] as IFetchBid[],
    isLoading : IsLoadingBids,
  } = useFetchAuctionBidData();

  const {
    data      : auctionsStatuses = [] as TStatusOption[],
    isLoading : isLoadingAuctionStatuses,
  } = useFetchAuctionsStatuses(
    `CUID eq '${cuid}'`,
    { enabled: !!cuid },
  );

  const {
    data      : auctionsResponse = {} as IAuctionsResponse,
    isLoading : isLoadingAuctions,
  } = useFetchAuctions(params, {
    enabled: !!cuid,
  });
  // #endregion

  const handleBidListModal = useCallback(
    (auction: IAuction) => {
      fetchBidData(auction?.id);
      toggleIsBidListModal();
    },
    [fetchBidData, toggleIsBidListModal],
  );

  const tableRows = useMemo(
    (): TDataTableRow[] =>
      getTableRows(
        auctionsResponse.auctions,
        auctionsStatuses,
        push,
        handleBidListModal,
      ),
    [push, handleBidListModal, auctionsResponse, auctionsStatuses],
  );

  return (
    <>
      <AccountAuctionsPage
        push                = {push}
        tableRows           = {tableRows}
        accountData         = {accountData}
        fetchAuctionsStatus = {isLoadingAuctions || isLoadingAuctionStatuses}
      />

      <BidListModal
        isOpen      = {isBidListModal}
        isLoading   = {IsLoadingBids}
        currentBid  = {currentBid}
        toggleModal = {toggleIsBidListModal}
      />
    </>
  );
};
